import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { weatherAPI } from '@/lib/api';
import { saveOffline, loadOffline, coordKey } from '@/lib/offlineStore';
import { 
  Cloud, Sun, CloudRain, CloudSnow, Wind, Waves, Eye, 
  Thermometer, Droplets, AlertTriangle, ChevronDown, ChevronUp,
  RefreshCw, X, CloudFog, CloudLightning, Loader2, ArrowUp, ArrowDown, WifiOff
} from 'lucide-react';

const WeatherWidget = ({ lat, lon, onClose }) => {
  const [weather, setWeather] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [tides, setTides] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isStale, setIsStale] = useState(false);

  const fetchWeather = useCallback(async () => {
    if (!lat || !lon) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [weatherRes, alertsRes, forecastRes, tidesRes] = await Promise.all([
        weatherAPI.getCurrent(lat, lon),
        weatherAPI.getAlerts(lat, lon),
        weatherAPI.getForecast(lat, lon, 3),
        weatherAPI.getTides(lat, lon).catch(() => ({ data: null })),
      ]);
      
      const payload = {
        weather: weatherRes.data,
        alerts: alertsRes.data.alerts || [],
        forecast: forecastRes.data.forecast || [],
        tides: tidesRes.data,
      };
      setWeather(payload.weather);
      setAlerts(payload.alerts);
      setForecast(payload.forecast);
      setTides(payload.tides);
      setLastUpdate(new Date());
      setIsStale(false);
      // Cache for offline use
      saveOffline(coordKey('weather', lat, lon), payload);
    } catch (err) {
      console.error('Weather fetch error:', err);
      // Offline / server unreachable: fall back to last cached data
      const cached = await loadOffline(coordKey('weather', lat, lon));
      if (cached && cached.data) {
        setWeather(cached.data.weather);
        setAlerts(cached.data.alerts || []);
        setForecast(cached.data.forecast || []);
        setTides(cached.data.tides);
        setLastUpdate(new Date(cached.ts));
        setIsStale(true);
      } else {
        setError('Unable to load weather');
      }
    } finally {
      setLoading(false);
    }
  }, [lat, lon]);

  useEffect(() => {
    fetchWeather();
    // Refresh every 10 minutes
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWeather]);

  const getWeatherIcon = (code, isDay = true) => {
    if (code === 0 || code === 1) return isDay ? <Sun className="w-6 h-6 text-yellow-400" /> : <Sun className="w-6 h-6 text-gray-300" />;
    if (code === 2 || code === 3) return <Cloud className="w-6 h-6 text-gray-400" />;
    if (code === 45 || code === 48) return <CloudFog className="w-6 h-6 text-gray-400" />;
    if (code >= 51 && code <= 67) return <CloudRain className="w-6 h-6 text-blue-400" />;
    if (code >= 71 && code <= 86) return <CloudSnow className="w-6 h-6 text-blue-200" />;
    if (code >= 95) return <CloudLightning className="w-6 h-6 text-yellow-500" />;
    return <Cloud className="w-6 h-6 text-gray-400" />;
  };

  const getWindDirection = (degrees) => {
    if (degrees === null || degrees === undefined) return 'N/A';
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  // Plain-English sea state from wave height (feet)
  const getSeaState = (heightFt) => {
    if (heightFt === null || heightFt === undefined) return null;
    if (heightFt < 1) return { label: 'Calm', cls: 'bg-green-100 text-green-700', caution: false };
    if (heightFt < 2) return { label: 'Light chop', cls: 'bg-green-100 text-green-700', caution: false };
    if (heightFt < 4) return { label: 'Moderate chop', cls: 'bg-yellow-100 text-yellow-700', caution: false };
    if (heightFt < 6) return { label: 'Rough', cls: 'bg-orange-100 text-orange-700', caution: true };
    return { label: 'Very rough', cls: 'bg-red-100 text-red-700', caution: true };
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Extreme': return 'bg-red-600';
      case 'Severe': return 'bg-orange-500';
      case 'Moderate': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  if (loading && !weather) {
    return (
      <Card className="absolute top-20 left-4 z-[1000] bg-white/95 backdrop-blur shadow-lg w-64" data-testid="weather-widget">
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
          <span className="ml-2 text-sm">Loading weather...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="absolute top-20 left-4 z-[1000] bg-white/95 backdrop-blur shadow-lg w-64" data-testid="weather-widget">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-red-600">{error}</span>
            <Button size="sm" variant="ghost" onClick={fetchWeather}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="absolute top-20 left-4 z-[1000] bg-white/95 backdrop-blur shadow-lg w-72 max-h-[80vh] overflow-hidden" data-testid="weather-widget">
      <CardContent className="p-0">
        {/* Header with current conditions */}
        <div className="p-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {weather && getWeatherIcon(weather.weather_code, weather.is_day)}
              <span className="text-2xl font-bold">{weather?.temperature ?? '--'}°F</span>
            </div>
            <div className="flex gap-1">
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-white hover:bg-white/20 p-1 h-7 w-7"
                onClick={fetchWeather}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              {onClose && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-white hover:bg-white/20 p-1 h-7 w-7"
                  onClick={onClose}
                  data-testid="weather-close-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm opacity-90">{weather?.weather_description || 'Loading...'}</p>
          <p className="text-xs opacity-75">Feels like {weather?.feels_like ?? '--'}°F</p>
          {isStale && (
            <div className="mt-2 flex items-center gap-1 bg-white/20 rounded px-2 py-1 w-fit" data-testid="weather-cached-badge">
              <WifiOff className="w-3 h-3" />
              <span className="text-[11px]">
                Offline · saved{lastUpdate ? ` ${lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
              </span>
            </div>
          )}
        </div>

        {/* Alerts section */}
        {alerts.length > 0 && (
          <div className="p-3 bg-red-50 border-b border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-semibold text-red-800">Active Alerts ({alerts.length})</span>
            </div>
            {alerts.slice(0, 2).map((alert, idx) => (
              <div key={idx} className="mb-2 last:mb-0">
                <Badge className={`${getSeverityColor(alert.severity)} text-white text-xs`}>
                  {alert.severity}
                </Badge>
                <p className="text-xs text-red-800 mt-1 line-clamp-2">{alert.headline}</p>
              </div>
            ))}
          </div>
        )}

        {/* Marine conditions */}
        <div className="p-3 border-b">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Wind className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Wind</p>
                <p className="text-sm font-medium">
                  {weather?.wind_speed ?? '--'} mph {getWindDirection(weather?.wind_direction)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Thermometer className="w-4 h-4 text-orange-400" />
              <div>
                <p className="text-xs text-gray-500">Feels Like</p>
                <p className="text-sm font-medium">{weather?.feels_like ?? '--'}°F</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Visibility</p>
                <p className="text-sm font-medium">{weather?.visibility ?? '--'} mi</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Droplets className="w-4 h-4 text-blue-400" />
              <div>
                <p className="text-xs text-gray-500">Humidity</p>
                <p className="text-sm font-medium">{weather?.humidity ?? '--'}%</p>
              </div>
            </div>
          </div>
          {weather?.wind_gusts && weather.wind_gusts > weather.wind_speed && (
            <p className="text-xs text-orange-600 mt-2">
              ⚠️ Gusts up to {weather.wind_gusts} mph
            </p>
          )}
        </div>

        {/* Wave Report */}
        <div className="p-3 border-b bg-blue-50/50" data-testid="wave-report">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Waves className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-semibold text-gray-700">Wave Report</span>
            </div>
            {getSeaState(weather?.wave_height) && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getSeaState(weather?.wave_height).cls}`} data-testid="wave-sea-state">
                {getSeaState(weather?.wave_height).label}
              </span>
            )}
          </div>
          {weather?.wave_height !== null && weather?.wave_height !== undefined ? (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white rounded-lg py-1.5">
                  <p className="text-[10px] text-gray-500">Height</p>
                  <p className="text-sm font-semibold" data-testid="wave-height">{weather.wave_height} ft</p>
                </div>
                <div className="bg-white rounded-lg py-1.5">
                  <p className="text-[10px] text-gray-500">Period</p>
                  <p className="text-sm font-semibold">{weather.wave_period ?? '--'} s</p>
                </div>
                <div className="bg-white rounded-lg py-1.5">
                  <p className="text-[10px] text-gray-500">Direction</p>
                  <p className="text-sm font-semibold">{getWindDirection(weather.wave_direction)}</p>
                </div>
              </div>
              {getSeaState(weather?.wave_height)?.caution && (
                <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Rough seas — small-craft caution advised.
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-500" data-testid="wave-unavailable">
              Wave data unavailable for this location right now.
            </p>
          )}
        </div>

        {/* Tides */}
        {tides && tides.status === 'ok' && (
          <div className="p-3 border-b bg-cyan-50/50" data-testid="tides-report">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Waves className="w-4 h-4 text-cyan-600" />
                <span className="text-sm font-semibold text-gray-700">Tides</span>
              </div>
              {tides.next_tide && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 flex items-center gap-1" data-testid="tides-next">
                  {tides.next_tide.type === 'High' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  Next {tides.next_tide.type} {tides.next_tide.time.slice(11, 16)}
                </span>
              )}
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {tides.tides?.map((e, i) => (
                <div key={i} className="bg-white rounded-lg px-2 py-1.5 text-center flex-shrink-0 min-w-[64px]">
                  <p className={`text-[10px] font-medium ${e.type === 'High' ? 'text-blue-600' : 'text-amber-600'}`}>{e.type}</p>
                  <p className="text-xs font-semibold">{e.time.slice(11, 16)}</p>
                  <p className="text-[10px] text-gray-500">{e.height_ft} ft</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-1 truncate">{tides.station_name} · {tides.distance_nm} nm away</p>
          </div>
        )}
        {tides && tides.status === 'no_station' && (
          <div className="p-3 border-b text-xs text-gray-500" data-testid="tides-none">
            No tide station nearby — negligible tide here (inland / Great Lakes).
          </div>
        )}

        {/* Expandable forecast */}
        <div className="p-3">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full flex items-center justify-between text-gray-600"
            onClick={() => setExpanded(!expanded)}
            data-testid="weather-expand-btn"
          >
            <span className="text-sm">3-Day Forecast</span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          
          {expanded && (
            <div className="mt-2 space-y-2">
              {forecast.map((day, idx) => {
                // Get today's date in YYYY-MM-DD format for comparison
                const today = new Date().toISOString().split('T')[0];
                const forecastDate = day.date;
                const isToday = forecastDate === today;
                
                // Determine display label
                let dayLabel;
                if (isToday) {
                  dayLabel = 'Today';
                } else {
                  // Show weekday for future days
                  const date = new Date(forecastDate + 'T12:00:00'); // Add time to avoid timezone issues
                  dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });
                }
                
                return (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      {getWeatherIcon(day.weather_code, true)}
                      <span className="text-sm">{dayLabel}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{day.temp_high}° / {day.temp_low}°</p>
                      <p className="text-xs text-gray-500">{day.precipitation_probability}% rain</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Last update */}
        {lastUpdate && (
          <div className="px-3 pb-2">
            <p className="text-xs text-gray-400 text-center">
              Updated {lastUpdate.toLocaleTimeString()}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WeatherWidget;
